import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  aujourdhuiParis,
  estJour,
  VUES_CALENDRIER,
  type Jour,
  type VueCalendrier,
} from './periodes.js';

const PREFIXE_CLE = 'artosera.calendrier.';

function estVue(valeur: string | null): valeur is VueCalendrier {
  return (VUES_CALENDRIER as readonly (string | null)[]).includes(valeur);
}

function vueMemorisee(cle: string): VueCalendrier | null {
  try {
    const valeur = window.localStorage.getItem(PREFIXE_CLE + cle);
    return estVue(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

function memoriserVue(cle: string, vue: VueCalendrier): void {
  try {
    window.localStorage.setItem(PREFIXE_CLE + cle, vue);
  } catch {
    // Stockage refusé (navigation privée) : la vue reste dans l'URL.
  }
}

/**
 * La vue et la période d'un calendrier. La période (`?date=AAAA-MM-JJ`) vit dans
 * l'URL : elle survit au rechargement et au retour depuis une fiche. La vue
 * (`?vue=`) y vit aussi, et la dernière vue choisie est retenue dans le
 * navigateur (`cle` : une mémoire par page) pour la prochaine visite. Sans
 * rien de tout cela : `vueParDefaut`, aujourd'hui (à Paris).
 */
export function useVueCalendrier(
  cle: string,
  vueParDefaut: VueCalendrier = 'liste',
): {
  vue: VueCalendrier;
  ancre: Jour;
  /** `ancre` absente : retour à aujourd'hui. */
  aller: (vue: VueCalendrier, ancre?: Jour) => void;
} {
  const [params, setParams] = useSearchParams();
  const vueUrl = params.get('vue');
  const dateUrl = params.get('date');
  const vue = estVue(vueUrl) ? vueUrl : (vueMemorisee(cle) ?? vueParDefaut);
  const ancre = estJour(dateUrl) ? dateUrl : aujourdhuiParis();

  const aller = useCallback(
    (nouvelleVue: VueCalendrier, nouvelleAncre?: Jour) => {
      memoriserVue(cle, nouvelleVue);
      setParams(
        (courants) => {
          const suivants = new URLSearchParams(courants);
          suivants.set('vue', nouvelleVue);
          if (nouvelleAncre) suivants.set('date', nouvelleAncre);
          else suivants.delete('date');
          return suivants;
        },
        { replace: true },
      );
    },
    [cle, setParams],
  );

  return { vue, ancre, aller };
}
